import React, { forwardRef, useState } from 'react';

import { Autocomplete, InputAdornment, InputBase, Typography } from '@mui/material';
import type { AutocompleteChangeReason } from '@mui/material/useAutocomplete';
import { Search } from 'lucide-react';
import { AlineCheckbox } from 'src/components';
import { orderedReminderTypes } from 'src/components/reminders/details/ReminderType';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

type ReminderTypeAutocompleteValue = Apollo.ReminderType | null;

const useReminderTypeChooserStyles = makeStyles()(theme => ({
  autocompleteOption: {
    padding: theme.spacing(1),
  },
  disablePortal: {
    position: 'static',
  },
  input: {
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    padding: theme.spacing(1),
  },
  listPaper: {
    borderRadius: 0,
    margin: 'unset',
  },
}));
interface ReminderTypeChooser {
  isPopperOpen: boolean;
  onChange: (
    event: React.ChangeEvent<unknown>,
    value: ReminderTypeAutocompleteValue,
    reason: AutocompleteChangeReason
  ) => void;
  selectedTypes: Apollo.ReminderType[];
  className?: string;
  disabled?: boolean;
  getIsOptionDisabled?: (option: string) => boolean;
  placeholder?: string;
}

export const ReminderTypeChooser = forwardRef(function ReminderTypeChooser(
  props: ReminderTypeChooser,
  ref: React.Ref<HTMLDivElement>
) {
  const {
    className,
    disabled,
    getIsOptionDisabled,
    onChange,
    placeholder = 'Search types...',
    selectedTypes,
  } = props;

  const { classes } = useReminderTypeChooserStyles();
  const [inputValue, setInputValue] = useState<string>('');
  const selectedTypesSet = new Set(selectedTypes);

  return (
    <Autocomplete<Apollo.ReminderType>
      ref={ref}
      disableCloseOnSelect
      disablePortal
      open
      className={className}
      classes={{
        option: classes.autocompleteOption,
        paper: classes.listPaper,
        popperDisablePortal: classes.disablePortal,
      }}
      disabled={disabled}
      getOptionDisabled={getIsOptionDisabled}
      getOptionLabel={option => option}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => option === value}
      options={orderedReminderTypes}
      renderInput={props => {
        const { slotProps, ...rest } = props;

        return (
          <InputBase
            ref={slotProps.input.ref}
            autoFocus
            className={classes.input}
            inputProps={slotProps.htmlInput}
            placeholder={placeholder}
            startAdornment={
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            }
            {...rest}
          />
        );
      }}
      renderOption={({ key, ...props }, option) => (
        <li key={key} {...props}>
          <TypeOption option={option} selected={selectedTypesSet.has(option)} />
        </li>
      )}
      value={null}
      onChange={onChange}
      onInputChange={(_, value, reason) => {
        switch (reason) {
          case 'reset':
          case 'selectOption':
            return;
          default:
            setInputValue(value);
            break;
        }
      }}
    />
  );
});

const useOptionStyles = makeStyles()(theme => ({
  option: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
}));

function TypeOption({
  option,
  selected,
}: {
  option: Apollo.ReminderType;
  selected: boolean;
}): JSX.Element {
  const { classes } = useOptionStyles();
  return (
    <div className={classes.option}>
      <AlineCheckbox checked={selected} />
      <Typography noWrap>{option}</Typography>
    </div>
  );
}
